<template>
  <v-card>
    <v-card-title class="pb-0">
      <h1 class="headline">
        Редактирование Перевозчика
      </h1>
    </v-card-title>
    <v-card-text class="pt-2">
      <v-form v-model="isValid">
        <v-flex xs12>
          <TitleTextField
            v-model="entity.title"
            :required="true"
            :rules="[rules.required, rules.min]"
            label="Наименование*"
            class="mx-2 mb-3"
            @input="isChanged = true"
          />
        </v-flex>
        <v-layout row>
          <v-flex xs6>
            <EditableList
              v-model="entity.phones"
              class="mx-2 mb-3"
              label="Телефон*"
              :mask="getMask('phone')"
              :hint="hint"
              filter="phoneInternal"
              outline
              height="44"
              title="Редактирование номеров телефонов"
              data-test="phone"
              @change="changePhones"
            />
          </v-flex>
          <v-flex xs6>
            <EditableList
              v-model="entity.emails"
              class="mx-2 mb-3"
              label="Email"
              :rules="[rules.email]"
              outline
              height="44"
              title="Редактирование e-mail"
              data-test="email"
              @change="changeEmails"
            />
          </v-flex>
        </v-layout>

        <v-layout row>
          <v-flex xs6>
            <app-reference
              v-model="entity.address"
              class="mx-2 mb-3"
              resource="addresses"
              component-name="Address"
              label="Адрес"
              :outline="true"
              :height-field="44"
              @input="isChanged = true"
            />
          </v-flex>
          <v-flex xs6>
            <app-reference
              v-model="entity.legalAddress"
              class="mx-2 mb-3"
              resource="addresses"
              component-name="Address"
              label="Юр. адрес"
              :outline="true"
              :height-field="44"
              @input="isChanged = true"
            />
          </v-flex>
        </v-layout>

        <v-layout row>
          <v-flex xs6>
            <MultipleEnumSelect
              v-model="entity.transportTypes"
              class="mx-2 mb-3"
              label="Типы перевозок"
              resource="transport-types"
              :return-object="true"
              :outline="true"
              :height-field="44"
              @input="isChanged = true"
            />
          </v-flex>
          <v-flex xs6>
            <MultipleReferenceCompact
              v-model="entity.countries"
              label="Страны перевозок"
              resource="countries"
              component-name="Country"
              :disabled-add="false"
              :outline="true"
              :height-field="44"
              class="mx-2 mb-3"
              @input="isChanged = true"
            />
          </v-flex>
        </v-layout>

        <v-layout row>
          <v-flex xs12>
            <app-reference-list
              v-model="entity.rollingStocks"
              class="mx-2 mb-3"
              label="Подвижной состав"
              resource="rolling-stocks"
              :return-object="true"
              :outline="true"
              :height-field="44"
              :columns="rollingStocksColumns"
              search-placeholder="Подвижной состав"
              reference-component="RollingStock"
              data-test="rollingStock"
              @input="isChanged = true"
            />
          </v-flex>
        </v-layout>

        <v-layout class="mt-3">
          <v-btn
            small
            color="primary"
            :disabled="!(isValid && isChanged) || isRemoved || loading"
            @click="submit(false)"
          >
            Сохранить
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="!(isValid && isChanged) || isRemoved || loading"
            @click="saveAndClose"
          >
            Сохранить и закрыть
          </v-btn>
          <v-btn
            small
            @click="cancel"
          >
            Отмена
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="id!==0"
            small
            :color="isRemoved ? 'success' :'error'"
            @click="changeRemovedState"
          >
            {{ isRemoved ? "Восстановить" : "Удалить" }}
          </v-btn>
        </v-layout>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import mixin from "./../../mixins/confirm-cancel-changes.js";
import { NestedEditorsEventBus } from "./../../components/nested-editors/nested-editors-event-bus.js";
import * as carriersApi from "../../backend/carriersApi.js";

export default {
  mixins: [mixin],
  props: ["id"],
  data() {
    return {
      isValid: false,
      isChanged: false,
      rules: {
        empty: (v) => !!v || "",
        required: (v) => !!v || "Поле обязательно к заполнению",
        min: (v) =>
          v.length >= 3 || "Поле должно содержать не менее 3-х символов",
        email: (v) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return v && v.length > 0 && !pattern.test(v)
            ? "Не корректно введен e-mail."
            : true;
        },
      },
      resource: "carriers",
      addMessage: "Перевозчик добавлен",
      editMessage: "Перевозчик изменен",
      entity: {
        id: 0,
        title: "",
        phones: [],
        emails: [],
        address: null,
        legalAddress: null,
        transportTypes: [],
        countries: [],
        rollingStocks: [],
      },
      rollingStocksColumns: [
        { title: "Наименование", name: "title" },
        { title: "Тип", name: "type", data: "type.title" },
        { title: "Объем", name: "volume" },
        { title: "Грузоподъемность", name: "carryingCapacity" },
        { title: "Количество", name: "quantity" },
      ],
      isRemoved: false,
      loading: false,
      hint: "Номер телефона уже добавлен",
    };
  },
  computed: {
    isModal() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options._componentTag === "NestedEditors") {
          return true;
        }
        parent = parent.$parent;
      }
      return false;
    },
  },
  created() {
    if (+this.id) {
      this.getData();
    }
  },
  mounted() {
    if (this.isModal) {
      NestedEditorsEventBus.$emit("modal-editor-mounted");
    }
  },
  methods: {
    async getData() {
      const res = await carriersApi.getCarrier(this.resource, this.id);
      this.entity = {
        title: res.data.Title,
        phones: res.data.Phones,
        emails: res.data.Emails,
        address: res.data.Address
          ? {
            id: res.data.Address.Id,
            title: res.data.Address.Title,
          }
          : null,
        legalAddress: res.data.LegalAddress
          ? {
            id: res.data.LegalAddress.Id,
            title: res.data.LegalAddress.Title,
          }
          : null,
        transportTypes: res.data.TransportTypes.map((r) => {
          return {
            id: r.Id,
            title: r.Title,
          };
        }),
        countries: res.data.Countries.map((r) => {
          return {
            id: r.Id,
            title: r.Title,
          };
        }),
        rollingStocks: res.data.RollingStocks.map((r) => {
          return {
            id: r.Id,
            title: r.Title,
            volume: r.Volume,
            carryingCapacity: r.CarryingCapacity,
            type: r.Type.Title,
            quantity: r.Quantity,
          };
        }),
        isRemoved: res.data.IsRemoved,
      };
    },
    async submit(closeAfterSubmit) {
      const transportTypes = [];
      if (this.entity.transportTypes != null) {
        this.entity.transportTypes.map((t) => transportTypes.push(t.id));
      }
      const countries = [];
      if (this.entity.countries != null) {
        this.entity.countries.map((c) => countries.push(c.id));
      }
      const rollingStocks = [];
      if (this.entity.rollingStocks != null) {
        this.entity.rollingStocks.map((rs) => rollingStocks.push(rs.id));
      }
      const carrier = {
        title: this.entity.title,
        phones: this.entity.phones,
        emails: this.entity.emails,
        addressId: this.entity.address?.id,
        legalAddressId: this.entity.legalAddress?.id,
        transportTypes,
        countries,
        rollingStocks,
      };

      if (this.id && this.id > 0) {
        this.loading = true;
        await carriersApi.updateCarrier(this.resource, this.id, carrier);
        this.showInfo(this.editMessage);
        this.isChanged = false;
        if (closeAfterSubmit) {
          this.$router.go(-1);
        }
        this.getData();
        this.loading = false;
      } else {
        this.loading = true;
        await carriersApi.createCarrier(this.resource, carrier);
        this.showInfo(this.addMessage);
        this.isChanged = false;
        this.loading = false;
        if (this.isModal) {
          NestedEditorsEventBus.$emit("create", this.entity.id);
        } else {
          if (closeAfterSubmit) {
            this.$router.go(-1);
          }
        }
      }
    },
    cancel() {
      if (this.isModal) {
        NestedEditorsEventBus.$emit("close");
        return;
      } else {
        this.$router.go(-1);
      }
    },
    async saveAndClose() {
      await this.submit(true);
    },
    async changeRemovedState() {
      await carriersApi.changeRemovedState(
        this.resource,
        this.id,
        this.isRemoved,
      );
      this.isRemoved = !this.isRemoved;
    },
    changePhones(val) {
      this.isChanged = true;
      this.entity.phones = val;
    },
    changeEmails(val) {
      this.isChanged = true;
      this.entity.emails = val;
    },
    getMask(controlType) {
      switch (controlType) {
        case "phone":
          return "+##(###)###-##-##";
        default:
          return "";
      }
    },
    resetLoaders() {
      this.loading = false;
    },
  },
};
</script>
